import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { AlertTriangle, LogOut } from 'lucide-react';
import { BRAND } from '../brand';

type Status = 'loading' | 'processing' | 'success' | 'error';

const InvitationAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { setSelectedTeamId, refreshTeams } = useTeam();
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState<string>('Processing invitation...');
  const [error, setError] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<number | null>(null);
  
  // Check if error indicates wrong user account
  const isWrongUserError = error && (
    error.includes('different account') ||
    error.includes('logged in as') ||
    error.includes('different user') ||
    error.includes('correct account')
  );

  useEffect(() => {
    const acceptInvitation = async () => {
      if (!token) {
        setStatus('error');
        setError('Invalid invitation link. The token is missing.');
        return;
      }

      // If user is not authenticated, redirect to login with returnTo
      if (!user) {
        navigate('/login', { 
          state: { returnTo: `/invitations/accept/${token}` },
          replace: true 
        });
        return;
      }

      setStatus('processing');
      setMessage('Accepting invitation...');

      try {
        const response = await axios.post(`/invitations/invitations/token/${token}/accept`);
        const invitation = response.data;
        
        if (invitation && invitation.team_id) {
          setTeamId(invitation.team_id);
          setStatus('success');
          setMessage('Invitation accepted successfully! Redirecting to team management...');
          
          // Refresh teams to ensure the new team is available
          await refreshTeams();
          
          // Set the selected team in context
          setSelectedTeamId(invitation.team_id);
          
          // Redirect to team management page (strategize tab is the default)
          setTimeout(() => {
            navigate(`/teams/strategize`);
          }, 1500);
        } else {
          setStatus('error');
          setError('Invitation accepted but team information is missing.');
        }
      } catch (err: any) {
        setStatus('error');
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to accept invitation.';
        setError(errorMessage);
      }
    };

    acceptInvitation();
  }, [token, user, navigate, setSelectedTeamId, refreshTeams]);

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center shadow-lg">
            <img src={BRAND.logoSrc} alt={BRAND.name} className="h-16 w-16" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
            Accept Invitation
          </h2>
        </div>

        {/* Status Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 space-y-6">
          {/* Loading/Processing State */}
          {(status === 'loading' || status === 'processing') && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <svg className="animate-spin h-12 w-12 text-green-600 dark:text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <svg className="h-12 w-12 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-green-700 dark:text-green-300 font-medium">{message}</p>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center space-y-6">
              {isWrongUserError ? (
                <>
                  {/* Wrong User Error - Special Handling */}
                  <div className="flex justify-center">
                    <div className="relative">
                      <div className="absolute inset-0 bg-amber-200 dark:bg-amber-900 rounded-full blur-xl opacity-50 animate-pulse"></div>
                      <div className="relative bg-amber-50 dark:bg-amber-900/30 rounded-full p-6 border-2 border-amber-200 dark:border-amber-700">
                        <AlertTriangle className="h-16 w-16 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-amber-200 dark:border-amber-700 shadow-lg">
                    <div className="space-y-4">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        Wrong Account
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {error}
                      </p>
                      <div className="pt-4">
                        <button
                          onClick={() => {
                            logout();
                            navigate('/login', { 
                              state: { returnTo: `/invitations/accept/${token}` },
                              replace: true 
                            });
                          }}
                          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                        >
                          <LogOut className="h-5 w-5" />
                          Log Out and Sign In with Correct Account
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-center">
                    <div className="relative">
                      <div className="absolute inset-0 bg-red-200 dark:bg-red-900 rounded-full blur-xl opacity-30"></div>
                      <div className="relative bg-red-50 dark:bg-red-900/30 rounded-full p-6 border-2 border-red-200 dark:border-red-700">
                        <svg className="h-12 w-12 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="text-red-700 dark:text-red-300 font-medium">
                    <p>{error || 'An error occurred.'}</p>
                  </div>
                </>
              )}
              <div className="pt-4 space-y-2">
                {!user ? (
                  <button
                    onClick={() => navigate('/login', { state: { returnTo: `/invitations/accept/${token}` } })}
                    className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                  >
                    Go to Login
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                    >
                      Go to Dashboard
                    </button>
                    {teamId && (
                      <button
                        onClick={async () => {
                          await refreshTeams();
                          setSelectedTeamId(teamId);
                          navigate(`/teams/strategize`);
                        }}
                        className="w-full py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
                      >
                        Go to Team Management
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitationAcceptPage;

